import { useAppSelector } from "@app/hooks";
import { LBtn, LModal, LTableResults, LTableSearch, Loader } from "@components";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { parseStatus } from "@helpers/enumHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { useApi } from "@services/useApi";
import type { AssessmentReportType } from "@services/apiService";
import {
    AssessmentDeletionMode,
    AssessmentResultsCode,
    AssessmentStatus,
    ExistingAssessment,
    ExistingAssessments
} from "@store/generalTypes";
import { format } from "date-fns";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
    AssessmentFilterListInitialState,
    getCalendar,
    getFilteredData,
    getFormList,
    getScoreList,
    getStatusList,
    getVisitList
} from "../components/filterHelper";
import { baseResultColumns, detailResultColumns } from "../helpers/resultsTableHelper";
import "../../../../../styles/Calendar.scss";

export const ResultsOverview = ({ ...props }: ResultsProps): JSX.Element => {
    const validateMethods = useForm();
    const { getAssessments, downloadAssessments, deleteAssessments, loading } = useApi();
    const [filters, setFilters] = useState(AssessmentFilterListInitialState);
    const [existingAssessments, setExistingAssessments] = useState<ExistingAssessments>({} as ExistingAssessments);
    const [data, setData] = useState<ExistingAssessment[]>([]);
    const location = useAppSelector((x) => x.orgLocations.currentLocation);
    const { isClinician, isClinicalAdmin, isResearcher } = useUserPermission({
        useCurrentLocation: true
    });
    const showDetails = isClinicalAdmin || isClinician;
    const showDropDown = isClinicalAdmin || isClinician || isResearcher;
    const [blobUrl, setBlobUrl] = useState("");
    const [downloadFileName, setDownloadFileName] = useState("");
    const blobRef = useRef<HTMLAnchorElement | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [selectedResultsIds, setSelectedResultsIds] = useState<number[]>([]);

    const isAnyCheckboxChecked = (): boolean => selectedResultsIds.length > 0;
    const canPerformAction = (): boolean => !loading && isAnyCheckboxChecked();

    const detailColumns = detailResultColumns;

    const detailedColumnsMemoized = useMemo(() => detailColumns, []);

    const handleSelectedAssessments = (rows: ExistingAssessment[]): void => {
        const assessmentIds = rows?.reduce(
            (ids, currentAssessment) => (currentAssessment ? [...ids, currentAssessment.id] : ids),
            Array<number>()
        );
        setSelectedResultsIds(assessmentIds);
    };

    useEffect(() => {
        if (blobUrl) {
            blobRef.current?.click();
        }

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    const handleDownloadAssessments = async (reportType: AssessmentReportType, fileName: string): Promise<void> => {
        const fileContent = await downloadAssessments(location!.id, selectedResultsIds, reportType);
        const file = new Blob([fileContent], { type: "text/csv;charset=utf-8" });
        setDownloadFileName(fileName);
        setBlobUrl(URL.createObjectURL(file));
    };

    const handleDeleteResults = async (deletionMode: AssessmentDeletionMode = "delete"): Promise<void> => {
        try {
            await deleteAssessments(location!.id, selectedResultsIds, deletionMode);
            await handleGetAssessments();
        } catch (err: any) {
            console.error(err);
        }

        deletionMode === "archive" ? toggleArchiveModal() : toggleDeleteModal();
    };

    const toggleDeleteModal = (): void => {
        setIsDeleteModalOpen((isOpen) => !isOpen);
    };

    const toggleArchiveModal = (): void => {
        setIsArchiveModalOpen((isOpen) => !isOpen);
    };

    const actionOptions = useMemo(
        () =>
            [
                {
                    optionName: "DOWNLOAD OVERVIEW",
                    disabled: !canPerformAction(),
                    children: (
                        <LBtn
                            classes="has-text-primary-blue has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            disabled={!canPerformAction()}
                            onClick={async () => await handleDownloadAssessments("overview", "LASSI-D Overview.csv")}
                        >
                            DOWNLOAD OVERVIEW
                        </LBtn>
                    )
                },
                {
                    optionName: "DOWNLOAD RESULTS",
                    disabled: !canPerformAction(),
                    children: (
                        <LBtn
                            classes="has-text-primary-blue has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            disabled={!canPerformAction()}
                            onClick={async () => await handleDownloadAssessments("results", "LASSI-D Results.csv")}
                        >
                            DOWNLOAD RESULTS
                        </LBtn>
                    )
                },
                {
                    optionName: "Archive",
                    disabled: !canPerformAction(),
                    children: (
                        <LBtn
                            classes="has-text-grey-dark has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            disabled={!canPerformAction()}
                            onClick={toggleArchiveModal}
                        >
                            ARCHIVE
                        </LBtn>
                    )
                },
                {
                    optionName: "Delete",
                    disabled: !canPerformAction(),
                    children: (
                        <LBtn
                            classes="has-text-danger has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            disabled={!canPerformAction()}
                            onClick={toggleDeleteModal}
                        >
                            DELETE
                        </LBtn>
                    )
                }
            ] as DropdownMenuOption[],
        [loading, selectedResultsIds, location]
    );

    const handleGetAssessments = async (): Promise<void> => {
        if (location) {
            const assessments = await getAssessments(
                location?.id,
                props.isArchive,
                undefined,
                undefined,
                undefined,
                undefined,
                true
            );
            setExistingAssessments(assessments);
            setData(getFilteredData(filters, assessments.items));
        }
    };

    useEffect(() => {
        setData([]);
        setSelectedResultsIds([]);
        void handleGetAssessments();
    }, [location, props.isArchive]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        const newFilter = { ...filters, searchText: e.target.value };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleStatusFilter = (e: ChangeEvent<HTMLInputElement>): void => {
        const statusCodes = [...filters.statuses];
        const newStatus = e.target.value as AssessmentStatus;
        const index = statusCodes.findIndex((sc) => sc === newStatus);
        if (e.target.checked) statusCodes.push(newStatus);
        else statusCodes.splice(index, 1);

        const newFilter = { ...filters, statuses: statusCodes };

        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleVisitFilter = (e: ChangeEvent<HTMLInputElement>): void => {
        const newVisits = [...filters.visits];
        const visit = Number(e.target.value);
        const index = newVisits.findIndex((sc) => sc === visit);
        if (e.target.checked) newVisits.push(visit);
        else newVisits.splice(index, 1);

        const newFilter = { ...filters, visits: newVisits };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleFormFilter = (e: ChangeEvent<HTMLInputElement>): void => {
        const newForms = [...filters.forms];
        const index = newForms.findIndex((f) => f === e.target.value);
        if (e.target.checked) newForms.push(e.target.value);
        else newForms.splice(index, 1);

        const newFilter = { ...filters, forms: newForms };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleResultsFilter = (e: ChangeEvent<HTMLInputElement>): void => {
        const newScores = [...filters.score];
        const result = e.target.value as AssessmentResultsCode;
        const index = newScores.findIndex((sc) => sc === result);
        if (e.target.checked) newScores.push(result);
        else newScores.splice(index, 1);

        const newFilter = { ...filters, score: newScores };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleCalendarFilter = (values: [Date] | [Date, Date]): void => {
        const newFilter = { ...filters, dateRange: values };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const handleResetDates = (): void => {
        const newFilter = { ...filters, dateRange: undefined };
        setFilters(newFilter);
        setData(getFilteredData(newFilter, existingAssessments.items));
    };

    const getStatusLabel = (): string => {
        if (filters.statuses.length === 1) return parseStatus(filters.statuses[0]);
        else if (filters.statuses.length > 1) {
            return `${parseStatus(filters.statuses[0])} +${filters.statuses.length - 1}`;
        }
        return "Status";
    };

    const getFormsLabel = (): string => {
        if (filters.forms.length === 1) return filters.forms[0];
        else if (filters.forms.length > 1) {
            return `${filters.forms[0]} +${filters.forms.length - 1}`;
        }
        return "Forms";
    };

    const getNumericLabel = (label: string, nums: number[]): string => {
        if (nums.length === 1) return `${label} ${nums[0]}`;
        else if (nums.length > 1) return `${label} ${nums[0]} +${nums.length - 1}`;
        return label;
    };

    const getScoreLabel = (): string => {
        if (filters.score.length === 1) return filters.score[0];
        else if (filters.score.length > 1) {
            return `${filters.score[0]} +${filters.score.length - 1}`;
        }
        return "Results";
    };

    const getDateRangeLabel = (): string => {
        if (filters.dateRange && filters.dateRange.length === 1) {
            const date = filters.dateRange[0];
            if (date) return format(date, "MM/dd");
        } else if (filters.dateRange && filters.dateRange.length === 2) {
            const startDate = filters.dateRange[0];
            const endDate = filters.dateRange[1];
            if (startDate && endDate) {
                return `${format(startDate, "MM/dd")} - ${format(endDate, "MM/dd")}`;
            }
        }
        return "Date";
    };

    return (
        <FormProvider {...validateMethods}>
            <LTableSearch
                numRecords={data.length}
                handleSearch={handleSearch}
                resultCountContainerSize="3"
                resultsLabel={props.isArchive ? "Archived Results" : "Results"}
            />
            <div className="columns">
                <div className="column is-narrow">
                    <LDropdownMenu
                        options={getStatusList(filters, existingAssessments.items, handleStatusFilter)}
                        label={getStatusLabel()}
                        id="statuses"
                        isSelected={filters.statuses.length > 0}
                    />
                </div>
                <div className="column is-narrow">
                    <LDropdownMenu
                        options={getVisitList(filters, existingAssessments.items, handleVisitFilter)}
                        label={getNumericLabel("Visit", filters.visits)}
                        id="visits"
                        isSelected={filters.visits.length > 0}
                    />
                </div>
                <div className="column is-narrow">
                    <LDropdownMenu
                        options={getFormList(filters, existingAssessments.items, handleFormFilter)}
                        label={getFormsLabel()}
                        id="forms"
                        isSelected={filters.forms.length > 0}
                    />
                </div>
                <div className="column is-narrow">
                    <LDropdownMenu
                        options={getScoreList(filters, existingAssessments.items, handleResultsFilter)}
                        label={getScoreLabel()}
                        id="results"
                        isSelected={filters.score.length > 0}
                    />
                </div>
                <div className="column is-narrow">
                    <LDropdownMenu
                        options={getCalendar(filters, handleCalendarFilter, handleResetDates)}
                        label={getDateRangeLabel()}
                        id="date"
                        isSelected={filters.dateRange && filters.dateRange.length > 0}
                        noHover
                        centerContent
                        toolTipText={
                            <span>
                                Double click for single date selection or
                                <br />
                                click two different dates for a range.
                            </span>
                        }
                    />
                </div>
                {showDropDown && (
                    <div
                        className="column is-outlined"
                        style={{ backgroundColor: "none", position: "absolute", right: "5%" }}
                    >
                        <LDropdownMenu
                            id="resultsOptions"
                            iconFamily="caret"
                            isRightAligned
                            label="Action"
                            labelProps={{ className: "has-text-grey-dark" }}
                            options={actionOptions}
                            type="button"
                            theme="light"
                        />
                    </div>
                )}
                {isDeleteModalOpen ? (
                    <LModal
                        title={
                            <p className="has-text-centered has-text-weight-bold is-size-3">
                                Delete {selectedResultsIds.length} results
                            </p>
                        }
                        fullHeight={false}
                        width={"s"}
                        hasCancelConfirmButtons
                        confirmText="Delete"
                        onConfirm={handleDeleteResults}
                        onClose={toggleDeleteModal}
                        confirmClasses="is-danger"
                        confirmLoading={loading}
                        divider={false}
                    >
                        <div className="is-flex is-flex-direction-column">
                            <p className="content has-text-centered">
                                {`Are you sure you want to delete the ${selectedResultsIds.length} selected results?`}
                            </p>
                            <p className="content has-text-centered">{"This action cannot be undone."}</p>
                        </div>
                    </LModal>
                ) : null}
                {isArchiveModalOpen ? (
                    <LModal
                        title={
                            <p className="has-text-centered has-text-weight-bold is-size-3">
                                Archive {selectedResultsIds.length} results
                            </p>
                        }
                        fullHeight={false}
                        width={"s"}
                        hasCancelConfirmButtons
                        confirmText="Archive"
                        onConfirm={async () => await handleDeleteResults("archive")}
                        onClose={toggleArchiveModal}
                        confirmLoading={loading}
                        divider={false}
                    >
                        <div className="is-flex is-flex-direction-column">
                            <p className="content has-text-centered">
                                {`Are you sure you want to archive the ${selectedResultsIds.length} selected results?`}
                            </p>
                        </div>
                    </LModal>
                ) : null}
            </div>
            <div>
                {!showDetails &&
                    LTableResults<ExistingAssessment>(
                        data,
                        baseResultColumns,
                        {
                            getRowId: (assessment) => assessment.id.toString(),
                            selectionResetKey: `${location?.id.toString() ?? ""}-${Boolean(props.isArchive).toString()}`
                        },
                        handleSelectedAssessments
                    )}
                {showDetails &&
                    LTableResults<ExistingAssessment>(
                        data,
                        detailedColumnsMemoized,
                        {
                            getRowId: (assessment) => assessment.id.toString(),
                            selectionResetKey: `${location?.id.toString() ?? ""}-${Boolean(props.isArchive).toString()}`
                        },
                        handleSelectedAssessments
                    )}
                {loading && <Loader />}
            </div>
            <a ref={blobRef} className="hidden" download={downloadFileName} href={blobUrl} />
        </FormProvider>
    );
};

interface ResultsProps {
    isArchive?: boolean;
}
